/**
 * BillingAdminDashboard Page - Sprint 25 P2
 * 
 * Admin-only dashboard for managing tenant billing.
 * Restricted to billing_admin role.
 * 
 * Features:
 * - Tenant list with plan, seat usage, billing status
 * - Quick status update actions
 * - Plan change history (expandable rows)
 * - CSV export for manual invoicing
 * - Filters by plan, status, seat threshold
 * 
 * Route: /admin/billing-dashboard
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, 
  Users, 
  CreditCard, 
  Download, 
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Search,
  Filter,
  ArrowUpDown,
  MoreHorizontal,
  Edit2,
  History
} from 'lucide-react';
import { api } from '../api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import BillingStatusBadge from '../components/billing/BillingStatusBadge';

// Status options for quick update
const STATUS_OPTIONS = [
  { value: 'paid', label: 'Paid' },
  { value: 'payment_pending', label: 'Payment Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'manual_billing', label: 'Manual Billing' },
  { value: 'trial', label: 'Trial' }
];

// Plan options for filtering
const PLAN_OPTIONS = [
  { value: 'team', label: 'Team' },
  { value: 'business', label: 'Business' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'enterprise_plus', label: 'Enterprise+' },
  { value: 'unlimited_enterprise', label: 'Unlimited' }
];

const BillingAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [events, setEvents] = useState({});
  
  // Filters
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [seatThreshold, setSeatThreshold] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (planFilter && planFilter !== 'all') params.plan_filter = planFilter;
      if (statusFilter && statusFilter !== 'all') params.status_filter = statusFilter;
      if (seatThreshold) params.seat_threshold = parseInt(seatThreshold);

      const [dashboardRes, statsRes] = await Promise.all([
        api.get('/billing/admin/dashboard', { params }),
        api.get('/billing/admin/dashboard/stats')
      ]);

      setTenants(dashboardRes.data.tenants || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load billing dashboard:', err);
      toast.error('Failed to load billing dashboard');
    } finally {
      setLoading(false);
    }
  }, [planFilter, statusFilter, seatThreshold]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const loadTenantEvents = async (tenantId) => {
    if (events[tenantId]) return; // Already loaded
    
    try {
      const res = await api.get(`/billing/tenant/${tenantId}/events?limit=5`);
      setEvents(prev => ({
        ...prev,
        [tenantId]: res.data.events || []
      }));
    } catch (err) {
      console.error('Failed to load events:', err);
    }
  };

  const handleRowExpand = (tenantId) => {
    if (expandedRow === tenantId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(tenantId);
      loadTenantEvents(tenantId);
    }
  };

  const handleStatusUpdate = async (tenantId, newStatus) => {
    try {
      await api.patch(`/billing/tenant/${tenantId}/billing-status`, {
        status: newStatus,
        notes: `Status updated via admin dashboard`
      });
      toast.success('Billing status updated');
      loadDashboard();
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update billing status');
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (planFilter && planFilter !== 'all') params.append('plan_filter', planFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status_filter', statusFilter);
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/billing/admin/dashboard/export?${params}`;
      window.open(url, '_blank');
      toast.success('CSV export started');
    } catch (err) {
      toast.error('Failed to export CSV');
    }
  };

  // Filter tenants by search term
  const filteredTenants = tenants.filter(t => 
    !searchTerm || 
    t.tenant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.tenant_id.includes(searchTerm)
  );

  const getSeatUsageColor = (percent) => {
    if (percent >= 100) return 'text-red-600 bg-red-50';
    if (percent >= 80) return 'text-amber-600 bg-amber-50';
    return 'text-emerald-600 bg-emerald-50';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="billing-admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing Admin Dashboard</h1>
          <p className="text-gray-500 mt-1">Manage tenant billing, plans, and invoicing</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={loadDashboard} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={handleExportCSV}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Tenants</p>
                  <p className="text-2xl font-bold">{stats.total_tenants}</p>
                </div>
                <Building2 className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Paid</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.by_status?.paid || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.by_status?.payment_pending || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">At Seat Limit</p>
                  <p className="text-2xl font-bold text-red-600">{stats.seat_alerts?.at_limit || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-600">Filters:</span>
            </div>
            
            <div className="flex-1 max-w-xs">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search tenants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Plans" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                {PLAN_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="Min seat %"
                value={seatThreshold}
                onChange={(e) => setSeatThreshold(e.target.value)}
                className="w-28"
              />
            </div>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setPlanFilter('all');
                setStatusFilter('all');
                setSeatThreshold('');
                setSearchTerm('');
              }}
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Tenant Billing Overview
          </CardTitle>
          <CardDescription>
            {filteredTenants.length} tenant{filteredTenants.length !== 1 ? 's' : ''} shown
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No tenants match the current filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-gray-600">
                    <th className="text-left py-3 px-4 font-medium">Tenant</th>
                    <th className="text-left py-3 px-4 font-medium">Plan</th>
                    <th className="text-left py-3 px-4 font-medium">Seats</th>
                    <th className="text-left py-3 px-4 font-medium">Status</th>
                    <th className="text-left py-3 px-4 font-medium">Notes</th>
                    <th className="text-right py-3 px-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTenants.map((tenant) => (
                    <React.Fragment key={tenant.tenant_id}>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-900">{tenant.tenant_name}</p>
                            <p className="text-xs text-gray-400">{tenant.tenant_id.slice(0, 8)}...</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="capitalize">
                            {tenant.plan.name}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getSeatUsageColor(tenant.seats.usage_percent)}`}>
                              {tenant.seats.current}/{tenant.seats.limit}
                            </span>
                            <span className="text-xs text-gray-400">
                              ({tenant.seats.usage_percent}%)
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <BillingStatusBadge status={tenant.billing_status} size="small" />
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-xs text-gray-600 truncate max-w-[200px]">
                            {tenant.billing_notes || '—'}
                          </p>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRowExpand(tenant.tenant_id)}
                            >
                              <History className="w-4 h-4 mr-1" />
                              {expandedRow === tenant.tenant_id ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </Button>
                            
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {STATUS_OPTIONS.map(opt => (
                                  <DropdownMenuItem
                                    key={opt.value}
                                    onClick={() => handleStatusUpdate(tenant.tenant_id, opt.value)}
                                    disabled={tenant.billing_status === opt.value}
                                  >
                                    Set as {opt.label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Row - Event History */}
                      {expandedRow === tenant.tenant_id && (
                        <tr className="bg-slate-50">
                          <td colSpan={6} className="py-4 px-6">
                            <div className="text-sm">
                              <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <History className="w-4 h-4" />
                                Recent Plan Changes
                              </h4>
                              {events[tenant.tenant_id]?.length > 0 ? (
                                <div className="space-y-2">
                                  {events[tenant.tenant_id].map((event, idx) => (
                                    <div key={idx} className="flex items-center gap-4 text-xs bg-white p-2 rounded border">
                                      <Badge variant="outline" className="text-xs">
                                        {event.event_type?.replace(/_/g, ' ')}
                                      </Badge>
                                      <span className="text-gray-600">
                                        {event.old_plan || event.old_status || '—'} → {event.new_plan || event.new_status || '—'}
                                      </span>
                                      <span className="text-gray-400">
                                        {event.timestamp ? new Date(event.timestamp).toLocaleDateString() : '—'}
                                      </span>
                                      <span className="text-gray-500 truncate max-w-xs">
                                        {event.notes || ''}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-gray-400 text-xs">No recent events</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingAdminDashboard;
